import { useState, useMemo } from 'react';
import { CoachLayout } from '@/components/coach/layout/CoachLayout';
import { sportConfig } from '@/lib/coachConstants';
import type { SportType } from '@/lib/coachConstants';
import { useKnowledgeEntries, useCreateKnowledgeEntry, useUpdateKnowledgeEntry, useDeleteKnowledgeEntry } from '@/hooks/useKnowledge';
import type { KnowledgeEntryResponse, KnowledgeEntryCreate } from '@/lib/templateApi';
import { cn } from '@/lib/utils';
import { Search, Plus, BookOpen, ExternalLink, Pencil, Check, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/feedback/alert-dialog';

const sportOptions: SportType[] = ['running', 'cycling', 'swimming', 'strength', 'crossfit', 'hyrox', 'multi-sport'];

export default function KnowledgeLibraryPage() {
  const { data, isLoading } = useKnowledgeEntries();
  const entries = data?.items ?? [];

  const createMutation = useCreateKnowledgeEntry();
  const updateMutation = useUpdateKnowledgeEntry();
  const deleteMutation = useDeleteKnowledgeEntry();

  const [search, setSearch] = useState('');
  const [sportFilter, setSportFilter] = useState<'All' | SportType>('All');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Partial<KnowledgeEntryCreate>>({});

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      if (search && !e.title.toLowerCase().includes(search.toLowerCase()) && !e.tags.some(t => t.toLowerCase().includes(search.toLowerCase()))) return false;
      if (sportFilter !== 'All' && e.sport !== sportFilter) return false;
      return true;
    });
  }, [entries, search, sportFilter]);

  const selected = entries.find((e) => e.id === selectedId);

  const handleCreate = async () => {
    try {
      const entry = await createMutation.mutateAsync({ title: 'New Entry', content: '', sport: 'running' });
      setSelectedId(entry.id);
      setDraft({ title: entry.title, content: entry.content, sport: entry.sport ?? 'running', tags: entry.tags, source: entry.source ?? undefined });
      setEditing(true);
      toast.success('New entry created');
    } catch {
      toast.error('Failed to create entry');
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    const name = selected.title;
    try {
      await deleteMutation.mutateAsync(selected.id);
      setSelectedId(null);
      setEditing(false);
      toast.success(`"${name}" deleted`);
    } catch {
      toast.error('Failed to delete entry');
    }
  };

  const handleStartEdit = (entry: KnowledgeEntryResponse) => {
    setDraft({ title: entry.title, content: entry.content, sport: entry.sport ?? 'running', tags: entry.tags, source: entry.source ?? undefined });
    setEditing(true);
  };

  const handleSaveEdit = async () => {
    if (!selected) return;
    try {
      await updateMutation.mutateAsync({ id: selected.id, payload: draft });
      setEditing(false);
      toast.success('Entry saved');
    } catch {
      toast.error('Failed to save entry');
    }
  };

  const updateDraft = (updates: Partial<KnowledgeEntryCreate>) => {
    setDraft((prev) => ({ ...prev, ...updates }));
  };

  if (isLoading) {
    return (
      <CoachLayout>
        <div className="flex items-center justify-center h-48">
          <p className="text-xs text-muted-foreground font-mono">Loading knowledge base...</p>
        </div>
      </CoachLayout>
    );
  }

  return (
    <CoachLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-foreground">Knowledge Base</h1>
            <p className="text-xs text-muted-foreground mt-0.5">Training knowledge that feeds the AI coach</p>
          </div>
          <Button size="sm" className="gap-1.5 bg-accent/90 hover:bg-accent text-accent-foreground text-xs font-mono uppercase tracking-wider" onClick={handleCreate} disabled={createMutation.isPending}>
            <Plus className="h-3.5 w-3.5" /> New Entry
          </Button>
        </div>

        {/* Search + Filter */}
        <div className="flex gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search knowledge..."
              className="w-full pl-9 pr-3 py-2 text-xs bg-card/30 border border-border/20 rounded-lg text-foreground placeholder:text-muted-foreground/50 focus:border-accent/40 focus:outline-none transition-colors" />
          </div>
          <select value={sportFilter} onChange={(e) => setSportFilter(e.target.value as 'All' | SportType)}
            className="text-[10px] font-mono uppercase tracking-wider bg-card/30 border border-border/20 rounded px-2 py-1.5 text-foreground focus:border-accent/40 focus:outline-none">
            <option value="All">All Sports</option>
            {Object.entries(sportConfig).map(([k, v]) => (<option key={k} value={k}>{v.label}</option>))}
          </select>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* List */}
          <div className="col-span-12 lg:col-span-5 space-y-2">
            {filtered.map((entry) => {
              const sport = entry.sport && (entry.sport in sportConfig) ? sportConfig[entry.sport as SportType] : null;
              return (
                <button key={entry.id} onClick={() => { setSelectedId(entry.id); setEditing(false); }}
                  className={cn('w-full text-left rounded-lg border p-4 transition-all', selectedId === entry.id ? 'border-accent/30 bg-accent/5' : 'border-border/15 bg-card/30 hover:bg-card/50')}>
                  <div className="flex items-start gap-3">
                    <BookOpen className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-foreground truncate">{entry.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {sport && <span className="text-[8px] font-mono uppercase px-1.5 py-0.5 rounded" style={{ background: `${sport.color}15`, color: sport.color }}>{sport.label}</span>}
                        <span className="text-[10px] text-muted-foreground">{entry.created_at.split('T')[0]}</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {entry.tags.map((tag) => (
                          <span key={tag} className="text-[8px] font-mono px-1 py-px rounded bg-border/15 text-muted-foreground">{tag}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
            {filtered.length === 0 && (
              <div className="rounded-lg border border-border/15 bg-card/20 p-8 text-center">
                <p className="text-xs text-muted-foreground">No entries found</p>
              </div>
            )}
          </div>

          {/* Detail */}
          <div className="col-span-12 lg:col-span-7">
            {selected ? (
              <div className="rounded-lg border border-border/15 bg-card/20 p-6 sticky top-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    {editing ? (
                      <div className="space-y-3">
                        <div className="space-y-1">
                          <label className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">Title</label>
                          <input type="text" value={draft.title ?? ''} onChange={e => updateDraft({ title: e.target.value })}
                            className="w-full text-xs bg-background/50 border border-border/20 rounded px-2 py-1.5 text-foreground focus:border-accent/40 focus:outline-none" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <label className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">Sport</label>
                            <select value={draft.sport ?? ''} onChange={e => updateDraft({ sport: e.target.value as SportType })}
                              className="w-full text-xs bg-background/50 border border-border/20 rounded px-2 py-1.5 text-foreground focus:border-accent/40 focus:outline-none capitalize">
                              {sportOptions.map(s => <option key={s} value={s}>{sportConfig[s].label}</option>)}
                            </select>
                          </div>
                          <div className="space-y-1">
                            <label className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">Source</label>
                            <input type="text" value={draft.source ?? ''} onChange={e => updateDraft({ source: e.target.value || undefined })}
                              className="w-full text-xs bg-background/50 border border-border/20 rounded px-2 py-1.5 text-foreground focus:border-accent/40 focus:outline-none" placeholder="e.g. Research paper" />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <label className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">Tags (comma-separated)</label>
                          <input type="text" value={(draft.tags ?? []).join(', ')} onChange={e => updateDraft({ tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                            className="w-full text-xs bg-background/50 border border-border/20 rounded px-2 py-1.5 text-foreground focus:border-accent/40 focus:outline-none" />
                        </div>
                        <div className="space-y-1">
                          <label className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">Content</label>
                          <textarea value={draft.content ?? ''} onChange={e => updateDraft({ content: e.target.value })}
                            className="w-full text-xs bg-background/50 border border-border/20 rounded px-2 py-1.5 text-foreground focus:border-accent/40 focus:outline-none resize-none h-40" />
                        </div>
                      </div>
                    ) : (
                      <>
                        <h2 className="text-base font-medium text-foreground mb-2">{selected.title}</h2>
                        <div className="flex items-center gap-3 mb-4">
                          {selected.sport && selected.sport in sportConfig && (
                            <span className="text-[9px] font-mono uppercase px-2 py-0.5 rounded" style={{ background: `${sportConfig[selected.sport as SportType].color}15`, color: sportConfig[selected.sport as SportType].color }}>
                              {sportConfig[selected.sport as SportType].label}
                            </span>
                          )}
                          {selected.source && (
                            <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                              <ExternalLink className="h-3 w-3" /> {selected.source}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-foreground/80 leading-relaxed">{selected.content}</p>
                        <div className="flex flex-wrap gap-1.5 mt-4">
                          {selected.tags.map((tag) => (
                            <span key={tag} className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-border/20 text-muted-foreground">{tag}</span>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="flex gap-1 ml-3 flex-shrink-0">
                    <Button variant="ghost" size="icon" className={cn('h-7 w-7', editing ? 'text-accent' : 'text-muted-foreground hover:text-foreground')}
                      onClick={() => editing ? handleSaveEdit() : handleStartEdit(selected)}
                      disabled={updateMutation.isPending}>
                      {editing ? <Check className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-card border-border/30">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-foreground">Delete Entry?</AlertDialogTitle>
                          <AlertDialogDescription className="text-muted-foreground">
                            <span className="font-medium text-foreground">"{selected.title}"</span><br />
                            This will permanently delete this knowledge entry.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="text-xs font-mono uppercase tracking-wider">Cancel</AlertDialogCancel>
                          <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90 text-xs font-mono uppercase tracking-wider" onClick={handleDelete}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-border/15 bg-card/20 p-12 text-center">
                <p className="text-sm text-muted-foreground">Select an entry to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </CoachLayout>
  );
}
